import { useState, useEffect, useCallback, useRef } from 'react'
import debounce from 'lodash.debounce'
import lambdaFetch from '../functions/FetchFromLambda'

const API_CONFIG = {
  path: 'athena/lookup',
  method: 'post',
  defaultDebounceMs: 600
}

const useAthenaLookup = ({
  lookup,
  accessToken,
  credentials,
  debounceMs = API_CONFIG.defaultDebounceMs,
  filters = {},
  limit = 50,
  clearTrigger = false
}) => {
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [selectedValue, setSelectedValue] = useState(null)
  const [touched, setTouched] = useState(false)
  const [isIndexNotFound, setIsIndexNotFound] = useState(false)

  const normalizedFiltersRef = useRef({ signature: '', value: {} })
  const abortControllerRef = useRef(null)
  const requestIdRef = useRef(0)
  const mountedRef = useRef(true)
  const previousFiltersRef = useRef(null)

  const filtersSignature = JSON.stringify(filters)
  if (normalizedFiltersRef.current.signature !== filtersSignature) {
    normalizedFiltersRef.current = {
      signature: filtersSignature,
      value: Object.entries(filters).reduce((acc, [key, value]) => {
        if (value != null && value !== '') {
          acc[key] = value
        }
        return acc
      }, {})
    }
  }
  const normalizedFilters = normalizedFiltersRef.current.value

  if (previousFiltersRef.current == null) {
    previousFiltersRef.current = JSON.stringify(normalizedFilters)
  }

  const cleanFilters = useCallback(() => {
    return normalizedFilters
  }, [normalizedFilters])

  const clearOnFilterChange = useCallback(() => {
    const currentFilters = JSON.stringify(normalizedFilters)
    if (currentFilters !== previousFiltersRef.current) {
      requestIdRef.current += 1
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      setLoading(false)
      setOptions([])
      setSelectedValue(null)
      setInputValue('')
      previousFiltersRef.current = currentFilters
    }
  }, [normalizedFilters])

  const searchItems = useCallback(async (searchTerm = '') => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      const abortController = new AbortController()
      abortControllerRef.current = abortController

      setLoading(true)

      const response = await lambdaFetch(
        API_CONFIG.path,
        API_CONFIG.method,
        accessToken,
        JSON.stringify({
          lookup,
          filters: cleanFilters(),
          searchTerm,
          limit
        }),
        '',
        credentials,
        true,
        null,
        abortController.signal
      )

      if (!mountedRef.current || requestId !== requestIdRef.current) {
        return
      }

      if (response === false) {
        setOptions([])
        setIsIndexNotFound(false)
        return
      }

      const items = response?.data?.items || response?.items || []
      setOptions(items)
      setIsIndexNotFound(false)
    } catch (error) {
      if (error.name === 'AbortError') return
      if (!mountedRef.current || requestId !== requestIdRef.current) {
        return
      }
      setOptions([])
      setIsIndexNotFound(false)
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [accessToken, cleanFilters, credentials, limit, lookup])

  const debouncedSearch = useCallback(
    debounce((term) => {
      searchItems(term)
    }, debounceMs),
    [searchItems, debounceMs]
  )

  const handleInputChange = useCallback((newInputValue) => {
    setInputValue(newInputValue)
    setLoading(true)
    debouncedSearch(newInputValue.trim())
  }, [debouncedSearch])

  const handleChange = useCallback((newValue, onChange) => {
    setSelectedValue(newValue)
    setTouched(true)
    if (onChange) {
      onChange(newValue)
    }
    if (!newValue) {
      searchItems('')
    }
  }, [searchItems])

  const handleOpen = useCallback(() => {
    if (options.length === 0 && !loading) {
      searchItems('')
    }
  }, [loading, options.length, searchItems])

  const handleBlur = useCallback(() => {
    if (!selectedValue) {
      setTouched(true)
    }
  }, [selectedValue])

  const resetTouched = useCallback(() => {
    setTouched(false)
  }, [])

  const setValue = useCallback((value) => {
    setSelectedValue(value)
  }, [])

  const setInputValueDirect = useCallback((value) => {
    setInputValue(value)
  }, [])

  useEffect(() => {
    clearOnFilterChange()
  }, [clearOnFilterChange])

  useEffect(() => {
    if (clearTrigger) {
      setSelectedValue(null)
      setInputValue('')
      setTouched(false)
      setIsIndexNotFound(false)
    }
  }, [clearTrigger])

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  useEffect(() => {
    return () => {
      mountedRef.current = false
      requestIdRef.current += 1
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    loading,
    options,
    inputValue,
    selectedValue,
    touched,
    isIndexNotFound,
    handleInputChange,
    handleChange,
    handleOpen,
    handleBlur,
    resetTouched,
    setValue,
    setInputValueDirect,
    searchItems
  }
}

export default useAthenaLookup
